import { Injectable, inject } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    CanActivate,
    CanActivateFn,
    CanMatch,
    CanMatchFn,
    Route,
    Router,
    RouterStateSnapshot,
    UrlSegment,
    UrlTree,
} from '@angular/router';
import { PermissionService } from './permission.service';
import { AuthService } from '../auth/auth.service';
import { PermissionStrategy } from './permission.types';

/**
 * Route Guard untuk membatasi akses routing berdasarkan permission.
 *
 * Contoh konfigurasi route:
 * ```ts
 * {
 *   path: 'users',
 *   component: UsersComponent,
 *   canActivate: [PermissionGuard],
 *   data: {
 *     permission: 'user.read'
 *   }
 * }
 * ```
 *
 * Contoh multiple permissions:
 * ```ts
 * {
 *   path: 'finance',
 *   component: FinanceComponent,
 *   canActivate: [PermissionGuard],
 *   data: {
 *     permissions: ['invoice.read', 'transaction.read'],
 *     permissionStrategy: 'ANY', // atau 'ALL'
 *     redirectTo: '/403' // opsional
 *   }
 * }
 * ```
 */
@Injectable({
    providedIn: 'root',
})
export class PermissionGuard implements CanActivate, CanMatch {
    constructor(
        private _permissionService: PermissionService,
        private _authService: AuthService,
        private _router: Router
    ) {}

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): boolean | UrlTree {
        return this._checkPermission(route.data, state.url);
    }

    canMatch(route: Route, segments: UrlSegment[]): boolean | UrlTree {
        const fullUrl = '/' + segments.map((s) => s.path).join('/');
        return this._checkPermission(route.data || {}, fullUrl);
    }

    private _checkPermission(
        data: Record<string, any>,
        targetUrl: string
    ): boolean | UrlTree {
        // 1. Cek autentikasi dasar
        const isAuth = this._authService.isAuthenticated() || this._authService.isLoggedIn();
        if (!isAuth) {
            return this._router.createUrlTree(['/sign-in'], {
                queryParams: { redirectUrl: targetUrl },
            });
        }

        // 2. Ambil permission yang dibutuhkan dari route data
        const requiredPermission = data['permission'] || data['permissions'];
        const strategy: PermissionStrategy = data['permissionStrategy'] || 'ANY';
        const fallbackUrl = data['redirectTo'] || '/403';

        // Jika route tidak mendefinisikan permission apapun, izinkan akses
        if (!requiredPermission) {
            return true;
        }

        // 3. Evaluasi permission via PermissionService
        const isAllowed = this._permissionService.hasAccess(
            requiredPermission,
            strategy
        );

        if (isAllowed) {
            return true;
        }

        console.warn(
            `[PermissionGuard] Access denied for route "${targetUrl}". Required:`,
            requiredPermission,
            `Strategy: ${strategy}`
        );

        return this._router.createUrlTree([fallbackUrl]);
    }
}

/**
 * Functional Guard version untuk Angular 18
 */
export const permissionGuard: CanActivateFn = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
) => {
    const guard = inject(PermissionGuard);
    return guard.canActivate(route, state);
};

/**
 * Functional CanMatch Guard version untuk Angular 18
 */
export const permissionMatchGuard: CanMatchFn = (
    route: Route,
    segments: UrlSegment[]
) => {
    const guard = inject(PermissionGuard);
    return guard.canMatch(route, segments);
};
